import React, { useContext, useState, useEffect, useCallback, useRef } from "react";
import DealerTable from "../../components/Table/VehicleTable3";
import { FilterContext } from "../../context/Filter";
import { axInstBasic } from "../../components/Service/Service";

// Helper function
export const getArnPayload = (parArn, childArn, filters = {}) => {
  if (!parArn && !childArn) {
    throw new Error("No ARN provided for vehicle detail filter.");
  }

  return {
    ...filters,
    ...(parArn ? { parArn } : {}),
    ...(childArn ? { childArn } : {}),
  };
};

// API Call 1
const vehicleDetailReportOption = async (parArn, childArn, selectedReg, vehiclePayload, abortSignal) => {
  if (!selectedReg) return [];

  // Remove search_type and dropDownValue from vehiclePayload for this API
  const { search_type, dropDownValue, ...cleanPayload } = vehiclePayload || {};

  const payload = {
    ...getArnPayload(parArn, childArn, cleanPayload),
    search_type: selectedReg,
  };

  try {
    const res = await axInstBasic.post(
      `${process.env.REACT_APP_API_URL}vehicle-detail-report-option`,
      payload,
      { signal: abortSignal }
    );
    return Array.isArray(res.data?.result) ? res.data.result : [];
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error;
    }
    console.error("Error fetching vehicle-detail-report-option:", error);
    return [];
  }
};

// API Call 2
const vehicleDetailsReports = async (parArn, childArn, selectedReg, selectedValue, vehiclePayload, abortSignal) => {
  if (!selectedReg || !selectedValue) return [];

  // Remove search_type and dropDownValue from vehiclePayload for this API
  const { search_type, dropDownValue, ...cleanPayload } = vehiclePayload || {};

  const payload = {
    ...getArnPayload(parArn, childArn, cleanPayload),
    search_type: selectedReg,
    dropDownValue: selectedValue,
  };

  try {
    const res = await axInstBasic.post(
      `${process.env.REACT_APP_API_URL}vehicle-details-reports`,
      payload,
      { signal: abortSignal }
    );
    return Array.isArray(res.data?.result) ? res.data.result : [];
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error;
    }
    console.error("Error fetching vehicle-details-reports:", error);
    return [];
  }
};

const Detailedview = () => {
  const { arn: parArn, childArn, vehiclePayload, isFilterReady } = useContext(FilterContext);
  const shouldEnableQuery = Boolean(parArn || childArn);

  const [loading, setLoading] = useState(false);
  const [selectedReg, setSelectedReg] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [vehicleDetailReportOptionData, setVehicleDetailReportOptionData] = useState([]);
  const [vehicleDetailsReportsData, setVehicleDetailsReportsData] = useState([]);

  // Enhanced refs for better tracking
  const currentRequestRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastPayloadRef = useRef(null);
  const componentMountedRef = useRef(true);
  const resetInProgressRef = useRef(false);

  // Cancel any ongoing requests
  const cancelCurrentRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentRequestRef.current = null;
  }, []);

  // Enhanced reset function
  const resetState = useCallback(() => {
    resetInProgressRef.current = true;

    cancelCurrentRequests();
    setSelectedReg("");
    setSelectedValue("");
    setVehicleDetailReportOptionData([]);
    setVehicleDetailsReportsData([]);
    setLoading(false);

    // Clear the reset flag after a short delay
    setTimeout(() => {
      resetInProgressRef.current = false;
    }, 100);
  }, [cancelCurrentRequests]);

  // Enhanced first dropdown handler
  const handleFirstDropdownChange = async (e) => {
    const selectedFilter = e.target.value;

    // Prevent operations during reset
    if (resetInProgressRef.current) {
      return;
    }

    // Cancel any ongoing requests
    cancelCurrentRequests();

    setSelectedReg(selectedFilter);
    setSelectedValue("");
    setVehicleDetailsReportsData([]);
    setVehicleDetailReportOptionData([]);

    if (!selectedFilter || !shouldEnableQuery || !isFilterReady) {
      return;
    }

    setLoading(true);

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const requestId = Date.now();
    currentRequestRef.current = requestId;

    try {
      const options = await vehicleDetailReportOption(
        parArn,
        childArn,
        selectedFilter,
        vehiclePayload,
        abortControllerRef.current.signal
      );

      // Check if component is still mounted and request is still current
      if (componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        setVehicleDetailReportOptionData(options || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        console.error("Error fetching options:", error);
        setVehicleDetailReportOptionData([]);
      }
    } finally {
      if (componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        setLoading(false);
        currentRequestRef.current = null;
      }
    }
  };

  // Enhanced second dropdown handler
  const handleSecondDropdownChange = async (e) => {
    const selectedOption = e.target.value;

    // Prevent operations during reset
    if (resetInProgressRef.current) {
      return;
    }

    // Cancel any ongoing requests
    cancelCurrentRequests();

    setSelectedValue(selectedOption);
    setVehicleDetailsReportsData([]);

    if (!selectedOption || !selectedReg || !shouldEnableQuery || !isFilterReady) {
      return;
    }

    setLoading(true);

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const requestId = Date.now();
    currentRequestRef.current = requestId;

    try {
      const reports = await vehicleDetailsReports(
        parArn,
        childArn,
        selectedReg,
        selectedOption,
        vehiclePayload,
        abortControllerRef.current.signal
      );

      // Check if component is still mounted and request is still current
      if (componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        setVehicleDetailsReportsData(reports || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        console.error("Error fetching reports:", error);
        setVehicleDetailsReportsData([]);
      }
    } finally {
      if (componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        setLoading(false);
        currentRequestRef.current = null;
      }
    }
  };

  // Enhanced effect for payload changes
  useEffect(() => {
    if (!isFilterReady) {
      resetState();
      return;
    }

    const currentPayload = JSON.stringify(vehiclePayload);

    // If this is the first load, just set the reference
    if (lastPayloadRef.current === null) {
      lastPayloadRef.current = currentPayload;
      return;
    }

    // If payload has changed, reset everything
    if (lastPayloadRef.current !== currentPayload) {

      // Set reset flag to prevent race conditions
      resetInProgressRef.current = true;

      // Cancel any ongoing requests immediately
      cancelCurrentRequests();

      // Reset state
      setSelectedReg("");
      setSelectedValue("");
      setVehicleDetailReportOptionData([]);
      setVehicleDetailsReportsData([]);
      setLoading(false);

      // Update payload reference
      lastPayloadRef.current = currentPayload;

      // Clear reset flag after state updates
      setTimeout(() => {
        resetInProgressRef.current = false;
      }, 150);
    }
  }, [vehiclePayload, isFilterReady, resetState, cancelCurrentRequests]);

  // Cleanup on unmount
  useEffect(() => {
    componentMountedRef.current = true;

    return () => {
      componentMountedRef.current = false;
      cancelCurrentRequests();
    };
  }, [cancelCurrentRequests]);

  // Enhanced conditions for UI states
  const isSecondDropdownDisabled =
    !selectedReg ||
    !Array.isArray(vehicleDetailReportOptionData) ||
    vehicleDetailReportOptionData.length === 0 ||
    loading ||
    !isFilterReady ||
    resetInProgressRef.current;

  const showTable = vehicleDetailsReportsData.length > 0 &&
    !loading &&
    isFilterReady &&
    !resetInProgressRef.current;

  return (
    <div className="rounded-t-sm mt-2">
      <div className="bg-[#2374BF] p-1 text-md  text-white pb-1 pl-2 font-semibold  rounded-t-lg">Detailed View</div>

      <div className="block md:flex justify-items-center gap-20 px-4 py-3 items-center justify-center">
        {/* First Dropdown */}
        <div className="flex flex-col">
          <label htmlFor="filterBy" className="text-sm font-medium mb-1">Filter By</label>
          <select
            id="filterBy"
            value={selectedReg}
            onChange={handleFirstDropdownChange}
            disabled={loading || !isFilterReady || resetInProgressRef.current}
            className="border border-gray-300 w-64 font-medium rounded px-3 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {!isFilterReady ? "Loading..." : "Select Filter"}
            </option>
            <option value="chassis_number">Chassis Number</option>
            <option value="reg_number">Vehicle Registration Number</option>
            <option value="dealer_name">Dealer Name</option>
            <option value="year_range">Year Range</option>
          </select>
        </div>

        {/* Second Dropdown */}
        <div className="flex flex-col">
          <label htmlFor="selectValue" className="text-sm font-medium mb-1">Select Value</label>
          <select
            id="selectValue"
            value={selectedValue}
            onChange={handleSecondDropdownChange}
            disabled={isSecondDropdownDisabled}
            className="border border-gray-300 w-64 font-medium rounded px-3 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {!isFilterReady
                ? "Loading..."
                : resetInProgressRef.current
                  ? "Resetting..."
                  : !selectedReg
                    ? "Select Filter First"
                    : loading
                      ? "Loading options..."
                      : vehicleDetailReportOptionData.length === 0
                        ? "No options available"
                        : "Select Value"}
            </option>
            {vehicleDetailReportOptionData.map((item, idx) => (
              <option key={`${item.key}-${idx}`} value={item.key}>
                {item.key} {item.doc_count ? `(${item.doc_count})` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading */}
      {(loading || resetInProgressRef.current) && (
        <div className="text-center text-blue-600 mt-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2">
            {resetInProgressRef.current ? "Resetting..." : "Loading..."}
          </span>
        </div>
      )}

      {/* Conditional Messages */}
      {!shouldEnableQuery && (
        <p className="text-center text-red-600 mt-4">Context not ready. Please ensure ARN is set.</p>
      )}

      {!isFilterReady && shouldEnableQuery && (
        <p className="text-center text-gray-500 mt-4">Initializing filters...</p>
      )}

      {isFilterReady && shouldEnableQuery && !selectedReg && !loading && !resetInProgressRef.current && (
        <p className="text-center text-gray-500 mt-4">Please select a filter.</p>
      )}

      {selectedReg && !selectedValue && !loading && isFilterReady && vehicleDetailReportOptionData.length > 0 && !resetInProgressRef.current && (
        <p className="text-center text-gray-500 mt-4">Please select a value.</p>
      )}

      {selectedReg && !loading && isFilterReady && vehicleDetailReportOptionData.length === 0 && !resetInProgressRef.current && (
        <p className="text-center text-orange-600 mt-4">
          No options available for "{selectedReg}" filter.
        </p>
      )}

      {/* Table - Only show when we have data and are not loading/resetting */}
      {showTable && (
        <div className="mt-4">
          <DealerTable data={vehicleDetailsReportsData} />
        </div>
      )}

      {selectedReg && selectedValue && !loading && isFilterReady && vehicleDetailsReportsData.length === 0 && !resetInProgressRef.current && (
        <p className="text-center text-gray-500 mt-4">No data available for the selected criteria.</p>
      )}
    </div>
  );
};

export default Detailedview;